// Cipher Fitness — shared design tokens ("Training" environment: navy + gold)
// Single source of truth for color/type/spacing — import these instead of
// defining local `const colors = {...}` objects in individual components.
// Mirrors the shape of Cipher Nutrition's src/constants/theme.ts so the two
// apps share one design-system structure with different visual environments.

export const colors = {
  bg: '#0A0E27',
  surface1: '#10173A',
  surface2: '#161F49',
  surface3: '#1C2755',
  border: 'rgba(255, 255, 255, 0.08)',
  borderStrong: 'rgba(255, 255, 255, 0.14)',
  text: '#F3F5F9',
  textMuted: '#8B92AC',
  textSubtle: '#5D6480',
  // Reserved for: primary CTA, active nav, selected state, key metrics,
  // PR/milestone moments, small brand marks. Not for links, borders, or
  // decoration — see the design audit's gold rule.
  gold: '#D4AF37',
  goldDim: 'rgba(212, 175, 55, 0.14)',
  // The gold of a pressed button, and the dark end of a gold gradient.
  goldPressed: '#B8962E',
  success: '#6FBE8E',
  successDim: 'rgba(111, 190, 142, 0.12)',
  warning: '#FFD60A',
  danger: '#FF453A',
  dangerDim: 'rgba(255, 69, 58, 0.15)',
};

// One typeface for text, one for numbers, one for the wordmark. Loaded in public/index.html;
// the body font is also set in src/index.css.
export const fonts = {
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
  wordmark: "'Orbitron', sans-serif",
};

export const typography = {
  display: { fontSize: '40px', fontWeight: '800', lineHeight: '1.15', letterSpacing: '-0.01em' },
  screenTitle: { fontSize: '28px', fontWeight: '700', lineHeight: '1.25' },
  sectionHeading: { fontSize: '20px', fontWeight: '700', lineHeight: '1.3' },
  body: { fontSize: '15px', fontWeight: '400', lineHeight: '1.6' },
  metric: { fontSize: '40px', fontWeight: '700', lineHeight: '1', fontVariantNumeric: 'tabular-nums', fontFamily: "'JetBrains Mono', ui-monospace, monospace" },
  label: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' },
  caption: { fontSize: '12px', fontWeight: '500', lineHeight: '1.4' },
  mono: { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '13px' },
};

export const radius = {
  sm: '8px',
  card: '14px',
  modal: '20px',
  pill: '999px',
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
  '4xl': '64px',
};

export const shadows = {
  card: '0 4px 16px rgba(0,0,0,0.35)',
  elevated: '0 12px 32px rgba(0,0,0,0.5)',
  // Reserved for a completed/PR state — at most once per screen.
  goldGlow: '0 0 20px rgba(212,175,55,0.25)',
  successGlow: '0 0 16px rgba(111,190,142,0.3)',
};

// One easing curve app-wide — decelerate, no bounce.
const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';

export const transitions = {
  buttonPress: `all 120ms ${easing}`,
  component: `all 250ms ${easing}`,
  screen: `all 350ms ${easing}`,
  cinematicReveal: `all 650ms ${easing}`,
};

export const icon = {
  strokeWidth: 1.7,
};

const theme = {
  colors,
  fonts,
  typography,
  radius,
  spacing,
  shadows,
  transitions,
  icon,
};

export default theme;
